import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Truck, Phone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { name: "", contact_person: "", phone: "", email: "", address: "", tin: "", payment_terms: "", notes: "" };

const SuppliersPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setItems(data);
    } catch { toast.error("Failed to load suppliers"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) toast.error("Failed to load suppliers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name required"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/suppliers/${editing.supplier_id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Saved");
      setOpen(false); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${s.supplier_id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="suppliers-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Procurement</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Suppliers</h1>
          <p className="text-sm text-neutral-500 mt-1">Manage suppliers used for raw materials, payments and purchase orders.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="btn-accent inline-flex items-center gap-2" data-testid="supplier-create-button">
          <Plus className="h-4 w-4" /> Add supplier
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="pav-card text-center py-16" data-testid="suppliers-empty">
          <Truck className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
          <p className="text-neutral-500">No suppliers yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((s) => (
            <div key={s.supplier_id} className="pav-card" data-testid={`supplier-card-${s.supplier_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{s.name}</h3>
                  {s.contact_person && <p className="text-sm text-neutral-500">{s.contact_person}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); }}
                    data-testid={`supplier-edit-${s.supplier_id}`} className="p-1.5 hover:bg-[#F4F6FB] rounded">
                    <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                  </button>
                  <button onClick={() => remove(s)} data-testid={`supplier-delete-${s.supplier_id}`} className="p-1.5 hover:bg-red-50 rounded">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                {s.phone && <div className="flex items-center gap-2 text-neutral-600"><Phone className="h-3.5 w-3.5" />{s.phone}</div>}
                {s.email && <div className="flex items-center gap-2 text-neutral-600"><Mail className="h-3.5 w-3.5" />{s.email}</div>}
                {s.address && <p className="text-neutral-500 text-xs mt-1">{s.address}</p>}
                {s.tin && <p className="text-xs text-neutral-500">TIN: {s.tin}</p>}
                {s.payment_terms && <p className="text-xs text-[#06B6D4] font-semibold">{s.payment_terms}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Supplier name</label>
                <input className="pav-input mt-1" data-testid="supplier-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Contact person</label>
                <input className="pav-input mt-1" data-testid="supplier-contact-input"
                  value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Phone</label>
                <input className="pav-input mt-1" data-testid="supplier-phone-input"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Email</label>
                <input className="pav-input mt-1" type="email" data-testid="supplier-email-input"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Address</label>
                <textarea className="pav-input mt-1 min-h-[60px]" data-testid="supplier-address-input"
                  value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">TIN</label>
                <input className="pav-input mt-1" data-testid="supplier-tin-input"
                  value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Payment terms</label>
                <input className="pav-input mt-1" placeholder="e.g. Net 30" data-testid="supplier-terms-input"
                  value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Notes</label>
                <textarea className="pav-input mt-1 min-h-[60px]" data-testid="supplier-notes-input"
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="supplier-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save supplier
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
